import { Router, watchBusy } from "@frui.ts/screens/dist";
import BusyWatcher from "@frui.ts/screens/dist/structure/busyWatcher";
import { attachAutomaticValidator } from "@frui.ts/validation/dist";
import type { interfaces } from "inversify";
import { computed, observable } from "mobx";
import { editationNavigationName, Modules } from "../../../config";
import User, { BaseUserValidationRules } from "entities/user";
import UserRepository from "repositories/userRepository";
import ConfirmationService from "services/confirmationService";
import EventBus, { userUpdated } from "services/eventBus";
import LocalizationService from "services/localizationService";
import NotificationService, { NotificationType } from "services/notificationService";
import SecurityService from "services/securityService";
import EditableDetailViewModel from "../../editableDetailViewModel";

@Router.registerRoute({ route: editationNavigationName, name: Router.Self })
export default class UserEditationViewModel extends EditableDetailViewModel<User> {
  readonly modelName = "user";
  @observable busyWatcher: BusyWatcher = new BusyWatcher();

  constructor(
    public item: User,
    private repository: UserRepository,
    protected localizationService: LocalizationService,
    protected confirmationService: ConfirmationService,
    protected notificationService: NotificationService,
    protected securityService: SecurityService,
    protected eventBus: EventBus
  ) {
    super(item.id);
    this.navigationName = editationNavigationName;
  }

  @computed
  get name() {
    return this.allowWrite ? this.translate("name") : this.translate("nameReadOnly");
  }

  @watchBusy
  async onInitialize() {
    attachAutomaticValidator(this.item, BaseUserValidationRules);
    return super.onInitialize();
  }

  protected loadDetail(): Promise<User> | User {
    return this.item;
  }

  protected updateData(): Promise<User> {
    return this.repository.update(this.item);
  }

  protected onUpdated() {
    this.notificationService.add({ title: this.translate("updated"), type: NotificationType.SUCCESS });
    this.eventBus.publish(userUpdated({ user: this.item }));
  }

  @computed
  get allowWrite() {
    return this.securityService.allowToEntity("user", "write");
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.users, `users.userEditation.${code}`, size);
  }

  static Factory({ container }: interfaces.Context) {
    return (user: User) => {
      return new UserEditationViewModel(
        user,
        container.get(UserRepository),
        container.get(LocalizationService),
        container.get(ConfirmationService),
        container.get(NotificationService),
        container.get(SecurityService),
        container.get(EventBus)
      );
    };
  }
}
